// src/components/FeatureMatrix.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { getOrgs, listFeatureMatrix, getMatrix, bulkUpdateFeature } from "../api";
import ToggleCell from "./ToggleCell";
import {
  isOrgUnknown,
  mergeRefreshedAvailability,
  orgHasUnknownCell,
  keyFor,
} from "./orgAvailability";
import "../styles/feature-matrix.css";

export default function FeatureMatrix() {
  const [loading, setLoading] = useState(true);
  const [orgs, setOrgs] = useState([]);
  const [features, setFeatures] = useState([]);
  const [cells, setCells] = useState(() => new Map());
  const [saving, setSaving] = useState(() => new Set());
  const [error, setError] = useState("");
  // Org ids the backend could not read. Held separately from `cells` so an
  // outage is still reported when there are no cells to hang it on.
  const [unavailableOrgs, setUnavailableOrgs] = useState(() => new Set());
  const [featuresComplete, setFeaturesComplete] = useState(true);
  // Org ids the matrix response covered at all. null = backend sent no
  // availability block, so the "was this org even looked at" check is off.
  const [reportedOrgs, setReportedOrgs] = useState(null);

  const orgIsUnknown = useCallback(
    (orgId) => isOrgUnknown(orgId, unavailableOrgs, reportedOrgs),
    [unavailableOrgs, reportedOrgs]
  );

  const FEATURE_COL_WIDTH = 112;
  const ORG_COL_WIDTH = 240;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        // ONE unfiltered /matrix call for the whole grid.
        //
        // This used to be one /matrix per feature. Each of those is a full
        // fan-out across every tenant database, so 56 features x 50 orgs put
        // 2 800 connection attempts and 672 threads on a 512Mi backend at once.
        // On 2026-10-30 in ROC Gov it was SIGKILLed (exit 137) having answered
        // none of them, and the per-column .catch() turned every failure into
        // an empty column, so the grid rendered "-" everywhere with no error.
        //
        // Unfiltered, /matrix returns the same org x feature cross-product
        // from a single fan-out (~6s, ~290KB for 50 orgs x 56 features), and
        // every row names its own feature. The separate /features call is gone
        // with it: that endpoint builds its list from the same fan-out, so the
        // column headers come out of these rows instead of a second sweep.
        const [orgList, data] = await Promise.all([getOrgs(), listFeatureMatrix()]);
        if (cancelled) return;

        orgList.sort((a, b) => a.name.localeCompare(b.name));
        setOrgs(orgList);

        // Availability comes from `organizations`, not from the cells: when
        // every tenant is unreadable there are no feature names and therefore
        // no cells at all, and the grid would otherwise show a list of org
        // names with nothing beside them and no hint that anything is wrong.
        const unavail = new Set(
          (data.organizations || [])
            .filter((o) => o.available === false)
            .map((o) => o.org_id)
        );
        setUnavailableOrgs(unavail);
        setFeaturesComplete(data.features_complete !== false);

        // Which orgs the matrix actually reported on. The two endpoints are
        // separate snapshots taken at different moments, so /organizations can
        // still list an org the matrix response never covered -- it need only
        // be created between the two calls. (Creating one now invalidates both
        // caches, so a CACHED matrix is no longer the way this happens.)
        // Such an org has no availability
        // either way, and "not mentioned" is not evidence that its flags are
        // absent. null means an older backend that sends no `organizations`
        // block at all, where this check cannot apply.
        setReportedOrgs(
          Array.isArray(data.organizations)
            ? new Set(data.organizations.map((o) => o.org_id))
            : null
        );

        const next = new Map();
        const names = new Set();
        for (const row of data.items || []) {
          if (!row.feature_name) continue;
          names.add(row.feature_name);
          next.set(keyFor(row.org_id, row.feature_name), {
            exists: !!row.exists,
            enabled: row.enabled === null ? null : !!row.enabled,
            // Absent on an older backend, which only ever sent rows it had
            // actually read, so treat a missing field as available.
            available: row.available !== false,
          });
        }
        // Plain .sort() keeps the ASCII column order /features already gave.
        setFeatures([...names].sort());
        setCells(next);
      } catch (e) {
        setError(e?.message || "Failed to load matrix.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Every state write below is functional and touches ONE key. Snapshotting
  // `cells` or `saving` and setting the whole collection loses any change that
  // landed while this toggle was in flight: with two overlapping toggles the
  // second completion reinstated the first cell's old value, a rollback erased
  // the other cell's successful update, and a cell could stay stuck in
  // `saving` forever.
  const setCell = useCallback((k, value) => {
    setCells((prev) => {
      const next = new Map(prev);
      next.set(k, value);
      return next;
    });
  }, []);

  const markSaving = useCallback((k, on) => {
    setSaving((prev) => {
      const next = new Set(prev);
      if (on) next.add(k); else next.delete(k);
      return next;
    });
  }, []);

  const handleToggle = useCallback(
    async (orgId, feature) => {
      const k = keyFor(orgId, feature);
      const current = cells.get(k);
      if (!current || !current.exists) return;

      const nextEnabled = !(current.enabled === true);
      markSaving(k, true);
      setCell(k, { ...current, enabled: nextEnabled });

      try {
        // The write and the confirmation are separate failures and must not be
        // treated alike. POST /matrix/update answers 200 even when a tenant
        // write failed, and reports per-org `error` entries in the body.
        // api.js already throws on any entry in `failed`, so `row.error` below
        // is a second line of defence; `updated` is not covered there at all.
        const res = await bulkUpdateFeature(feature, [orgId], nextEnabled);
        const row = (res?.results || []).find((r) => r.org_id === orgId);
        if (!row) {
          throw new Error("the server reported no result for this org");
        }
        if (row.error) {
          throw new Error(row.error);
        }
        // This cell was `exists: true`, so a matching row had to be updated.
        // 0 means the write did not land, whatever the status code said.
        if (!row.updated) {
          throw new Error("the tenant reported no row updated");
        }
      } catch (e) {
        if (e?.uncertain) {
          // The commit was never acknowledged, so the tenant may hold either
          // value. Rolling back to the old one would put a definite "On" or
          // "Off" on screen for a write we cannot account for -- the same
          // false certainty this grid exists to avoid, and worse here because
          // the alert says one thing and the grid then says another.
          setCell(k, { ...current, available: false });
          setUnavailableOrgs((prev) =>
            mergeRefreshedAvailability(prev, orgId, false)
          );
        } else {
          setCell(k, current);
        }
        markSaving(k, false);
        alert(`Update failed for ${feature} @ org ${orgId}: ${e?.message || e}`);
        return;
      }

      // Past here the write is committed. A failure now is only a failure to
      // CONFIRM it, so the optimistic value stands and we say so rather than
      // rolling back a change the tenant database has already accepted.
      try {
        // One org, one feature. listFeatureMatrix(feature) would fan out to
        // every org to refresh a single cell, and the write above invalidates
        // the caches, so that refetch is always cold.
        const fresh = await getMatrix([orgId], [feature]);
        const match = (fresh?.items || []).find((x) => x.org_id === orgId);

        // A tenant whose read fails is NOT reported as an error: crud returns
        // [] and the matrix turns that into exists:false/enabled:null, exactly
        // like a flag that genuinely is not there. Trusting it here blanked a
        // cell that had just been written successfully, with no alert and no
        // warning.
        //
        // The write established that the row existed at commit time, which is
        // not the same as it existing now: the organization itself can be
        // deleted between the two calls, and then absence is authoritative.
        // We cannot tell that apart from a tenant read that simply failed,
        // because both arrive as exists:false. So keep the value we know was
        // committed and mark the cell UNCONFIRMED in the UI -- a console
        // warning is not something the person clicking will ever see.
        // A filtered refresh reports on this org only, so availability is
        // known for it and for nothing else; the rest of the set is left
        // alone, and featuresComplete is NOT touched because it describes the
        // original unfiltered discovery, which this request did not repeat.
        const refreshedAvailable = match ? match.available !== false : false;
        if (match && match.exists) {
          setCell(k, {
            exists: true,
            enabled: match.enabled === null ? null : !!match.enabled,
            available: refreshedAvailable,
          });
        } else {
          setCell(k, {
            exists: true,
            enabled: nextEnabled,
            available: refreshedAvailable,
            unconfirmed: true,
          });
        }
        // Keep the aggregate warning in step with the cell. Without this the
        // cell turned into "?" while the banner still said nothing was wrong.
        setUnavailableOrgs((prev) =>
          mergeRefreshedAvailability(prev, orgId, refreshedAvailable)
        );
      } catch (e) {
        // The re-read itself failed, so this org's availability is unknown
        // rather than known-bad; the cell carries the value we know committed.
        setCell(k, { exists: true, enabled: nextEnabled, unconfirmed: true });
        console.warn(
          `Saved ${feature} @ org ${orgId}, but could not re-read it:`,
          e
        );
      } finally {
        markSaving(k, false);
      }
    },
    [cells, setCell, markSaving]
  );

  // Counted off the orgs actually on screen, so an org the matrix never
  // covered is included and a stale id in the set is not. It also counts any
  // org that still has an unknown cell: a refresh reports on one cell but
  // replaces the whole org's availability, so a later successful save can
  // clear the org while an earlier "?" cell is still displayed.
  const unknownOrgCount = useMemo(
    () =>
      orgs.filter(
        (o) => orgIsUnknown(o.id) || orgHasUnknownCell(o.id, features, cells)
      ).length,
    [orgs, features, cells, orgIsUnknown]
  );

  const gridStyle = useMemo(
    () => ({
      gridTemplateColumns: `${ORG_COL_WIDTH}px repeat(${features.length}, ${FEATURE_COL_WIDTH}px)`,
      "--ff-cols": features.length,
      "--ff-orgw": `${ORG_COL_WIDTH}px`,
      "--ff-colw": `${FEATURE_COL_WIDTH}px`,
    }),
    [features.length]
  );

  if (loading) return <div className="container py-3">Loading feature matrix…</div>;
  if (error) return <div className="container py-3 text-danger">Error: {error}</div>;

  return (
    <div className="container-fluid py-3">
      <h4 className="mb-3">Feature Matrix (Orgs × Features)</h4>

      <div className="ff-matrix-scroll">
        <div className="ff-matrix" style={gridStyle}>
          <div className="ff-head ff-sticky-left">Organization</div>
          {features.map((f) => (
            <div className="ff-head" key={f} title={f}>
              <span className="ff-head-text">{f}</span>
            </div>
          ))}

          {orgs.map((org) => (
            <React.Fragment key={org.id}>
              <div className="ff-cell-org ff-sticky-left">
                {org.name}
              </div>
              {features.map((f) => {
                const k = keyFor(org.id, f);
                // A cell missing for an org we have no state for is unknown,
                // not absent — whether the tenant failed or the matrix never
                // covered that org at all.
                const cell =
                  cells.get(k) || {
                    exists: false,
                    enabled: null,
                    available: !orgIsUnknown(org.id),
                  };
                return (
                  <div className="ff-cell-wrap" key={`${org.id}::${f}`}>
                    <ToggleCell
                      exists={cell.exists}
                      value={cell.enabled}
                      available={cell.available}
                      saving={saving.has(k)}
                      unconfirmed={cell.unconfirmed}
                      onClick={() => handleToggle(org.id, f)}
                    />
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      {unknownOrgCount > 0 && (
        <div className="alert alert-warning py-2 px-3 mt-2 mb-0" role="status">
          {unknownOrgCount} organization
          {unknownOrgCount === 1 ? "" : "s"} could not be read, so their flag
          state is unknown.
          {!featuresComplete &&
            " Feature columns are discovered from the organizations that did" +
              " respond, so this list may be incomplete."}
        </div>
      )}

      <small className="text-muted d-block mt-2">
        “—” means the feature row doesn’t exist for that org yet. “?” on its
        own means that organization’s database could not be read, so its state
        is unknown. A value followed by “?” — “On?” or “Off?” — was written
        successfully but could not be read back, so what is shown is what was
        sent, not what the tenant returned.
      </small>
    </div>
  );
}
